import { Component, OnInit, ChangeDetectionStrategy, Input, Output, EventEmitter, AfterViewInit, OnChanges, SimpleChanges } from '@angular/core';
import { LastLoggedInUser } from '@core/models';
import { Validators, UntypedFormBuilder } from '@angular/forms';
import { trigger, state, transition, style, animate } from '@angular/animations';
import { ROUTE_ANIMATIONS_ELEMENTS } from '@core/animations/route.animations';
import { AuthenticateUserCommand } from '@core/services/api.service';
import { TwoFaDialogComponent } from '../two-fa-dialog/two-fa-dialog';
import { MatDialog } from '@angular/material/dialog';
import { Has2FAEnabledAction, Has2FAEnabledSuccessAction, LoadHasAuthenticatorConfiguredAction, LoadHasAuthenticatorConfiguredSuccessAction, LoadIsAccountLockedAction, LoadIsAccountLockedSuccessAction } from '@ngxs-store/auth-store/auth.actions';
import { Actions, Select, Store, ofActionSuccessful } from '@ngxs/store';
import { AuthState } from '@ngxs-store/auth-store/auth.state';
import { Observable, forkJoin } from 'rxjs';
import { QrCodeDialogComponent } from '../qr-code-dialog/qr-code-dialog';
import { ActivatedRoute } from '@angular/router';
import { take } from 'rxjs/operators';

@Component({
  selector: 'app-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [
    trigger('rotatedState', [
      state('default', style({ transform: 'rotate(0)' })),
      state('rotated', style({ transform: 'rotate(-360deg)' })),
      transition('rotated => default', animate('1500ms ease-out')),
      transition('default => rotated', animate('400ms ease-in'))
    ])
  ]
})
export class LoginComponent implements OnInit, AfterViewInit, OnChanges {
  @Input() isLoading: boolean;
  @Input() lastLoggedInUser: LastLoggedInUser;
  @Output() loginClicked: EventEmitter<AuthenticateUserCommand> = new EventEmitter();
  @Output() clearLastUserClicked: EventEmitter<void> = new EventEmitter();
  @Output() forgotPasswordClicked: EventEmitter<void> = new EventEmitter();
  @Select(AuthState.getHasAuthenticatorConfigured) hasAuthenticatorConfigured$: Observable<boolean>;
  @Select(AuthState.has2FAEnabled) has2FAEnabled$: Observable<boolean>;

  loginForm = this.fb.group({
    username: ['', Validators.required],
    password: ['', Validators.required]
  });
  username : string;
  isConfigured: boolean;
  isEnabled : boolean;
  type : string;

  state = 'default';
  routeAnimationsElements = ROUTE_ANIMATIONS_ELEMENTS;

  constructor(private fb: UntypedFormBuilder, public dialogRef: MatDialog, private store: Store, private route: ActivatedRoute,private actions$: Actions) {
    this.route.queryParams.subscribe(params => {

        const authorized = params['queryParams'];
        if (authorized === 'true') {
            this.loginClicked.emit(new AuthenticateUserCommand({
                username: this.loginForm.controls.username.value,
                password: this.loginForm.controls.password.value
            }));
        }
    });
   }

  ngOnInit() {
    if (this.lastLoggedInUser) {
      this.loginForm.controls.username.setValue(this.lastLoggedInUser.username);
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.isLoading && !changes.isLoading.firstChange) {
      changes.isLoading.currentValue ? this.loginForm.disable() : this.loginForm.enable();
    }
  }

  ngAfterViewInit() {
    this.rotate();
  }

  login() {
    console.log('The username and pass', this.loginForm.controls.username.value + this.loginForm.controls.password.value);
    if (this.loginForm.valid && this.loginForm.dirty) {
        this.username = this.loginForm.controls.username.value;

        // Dispatch actions
        this.store.dispatch(new LoadIsAccountLockedAction({ username: this.username }));
        this.store.dispatch(new Has2FAEnabledAction({ username: this.username }));
        this.store.dispatch(new LoadHasAuthenticatorConfiguredAction({ username: this.username }));

        forkJoin([
            this.actions$.pipe(ofActionSuccessful(LoadIsAccountLockedSuccessAction), take(1)),
            this.actions$.pipe(ofActionSuccessful(Has2FAEnabledSuccessAction), take(1)),
            this.actions$.pipe(ofActionSuccessful(LoadHasAuthenticatorConfiguredSuccessAction), take(1))
        ]).subscribe(([lockedresp, resp, response]: any[]) => {
            if (!lockedresp.payload.isAccountLocked) {
                if (resp.payload.has2FAEnabled) {
                    this.type = 'login';
                    if (response.payload.hasAuthenticatorConfigured) {
                        const dialogRef = this.dialogRef.open(TwoFaDialogComponent, {
                            data: { hasAuthenticatorConfigured: this.isConfigured, username: this.username, type: this.type }
                        });

                        dialogRef.afterClosed().subscribe(result => { });
                    } else {
                        const dialogRef = this.dialogRef.open(QrCodeDialogComponent, {
                            data: { hasAuthenticatorConfigured: this.isConfigured, username: this.username, type: this.type }
                        });

                        dialogRef.afterClosed().subscribe(result => { });
                    }
                } else {
                    this.loginClicked.emit(new AuthenticateUserCommand({
                        username: this.loginForm.controls.username.value,
                        password: this.loginForm.controls.password.value
                    }));
                }
            }
        });
    }
  }

  clearLastUser() {
    this.clearLastUserClicked.emit();
  }

  rotate() {
    this.state = (this.state === 'default' ? 'rotated' : 'default');
  }

  forgotPassword() {
    this.forgotPasswordClicked.emit();
  }
}
